import type { InteractionStep, OrchestratorAgent, SimpleAgentConfig } from '@demo-portal/shared';
import React, { useEffect, useState } from 'react';

interface InteractionFlowVisualizerProps {
  interactionFlow: InteractionStep[];
  orchestrator: OrchestratorAgent;
  agents: SimpleAgentConfig;
  isActive: boolean;
  onStepComplete: (step: InteractionStep) => void;
  onFlowComplete: () => void;
  primaryColor?: string;
}

interface FlowState {
  currentStepIndex: number;
  completedSteps: string[];
  activeStep: InteractionStep | null;
  isComplete: boolean;
}

const InteractionFlowVisualizer: React.FC<InteractionFlowVisualizerProps> = ({
  interactionFlow,
  orchestrator,
  agents,
  isActive,
  onStepComplete,
  onFlowComplete,
  primaryColor = '#3498db',
}) => {
  const [flowState, setFlowState] = useState<FlowState>({
    currentStepIndex: 0,
    completedSteps: [],
    activeStep: null,
    isComplete: false,
  });

  useEffect(() => {
    if (!isActive || interactionFlow.length === 0) {
      return;
    }

    const executeFlow = async () => {
      for (let i = 0; i < interactionFlow.length; i++) {
        const step = interactionFlow[i];
        
        // Check dependencies
        const dependenciesMet = step.dependencies.every(dep => 
          flowState.completedSteps.includes(dep)
        );
        
        if (!dependenciesMet && i > 0) {
          // Wait for dependencies
          await new Promise(resolve => setTimeout(resolve, 500));
        }

        // Set active step
        setFlowState(prev => ({
          ...prev,
          currentStepIndex: i,
          activeStep: step,
        }));

        // Execute step with duration
        await new Promise(resolve => setTimeout(resolve, step.duration));

        // Mark step as complete
        setFlowState(prev => ({
          ...prev,
          completedSteps: [...prev.completedSteps, step.stepId],
        }));

        onStepComplete(step);

        // Brief pause between steps
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      // Flow complete
      setFlowState(prev => ({
        ...prev,
        isComplete: true,
        activeStep: null,
      }));

      onFlowComplete();
    };

    executeFlow();
  }, [isActive, interactionFlow, onStepComplete, onFlowComplete]);

  const getAgentInfo = (agentId: string) => {
    if (agentId === 'orchestrator') {
      return {
        name: orchestrator.name,
        avatar: orchestrator.avatar,
        isOrchestrator: true,
      };
    }
    
    const agent = agents.find(a => a.id === agentId);
    return {
      name: agent?.name || 'Unknown Agent',
      avatar: agent?.avatar || '🤖',
      isOrchestrator: false,
    };
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'analyze': return '🧠';
      case 'call_agent': return '📞';
      case 'use_tool': return '🔧';
      case 'synthesize': return '⚡';
      default: return '⚙️';
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'analyze': return '#9b59b6';
      case 'call_agent': return '#3498db';
      case 'use_tool': return '#e67e22';
      case 'synthesize': return '#27ae60';
      default: return '#95a5a6';
    }
  };

  if (!isActive) {
    return (
      <div style={{
        background: '#f8f9fa',
        borderRadius: '12px',
        padding: '20px',
        textAlign: 'center',
        color: '#6c757d',
        border: '2px dashed #dee2e6',
      }}>
        <div style={{ fontSize: '2em', marginBottom: '10px' }}>⏳</div>
        <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
          Interaction Flow Visualizer
        </div>
        <div style={{ fontSize: '0.9em' }}>
          Waiting for orchestration to begin...
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: 'white',
      borderRadius: '15px',
      padding: '20px',
      boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
      border: `2px solid ${primaryColor}`,
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '20px',
        paddingBottom: '15px',
        borderBottom: `2px solid ${primaryColor}`,
      }}>
        <span style={{ fontSize: '1.5em', marginRight: '10px' }}>🔄</span>
        <div>
          <div style={{
            fontWeight: 'bold',
            fontSize: '1.1em',
            color: '#2c3e50',
          }}>
            Orchestration Flow
          </div>
          <div style={{
            fontSize: '0.85em',
            color: '#666',
          }}>
            Step {flowState.currentStepIndex + 1} of {interactionFlow.length}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{
        background: '#e9ecef',
        borderRadius: '10px',
        height: '6px',
        marginBottom: '20px',
        overflow: 'hidden',
      }}>
        <div style={{
          background: `linear-gradient(90deg, ${primaryColor}, ${primaryColor}aa)`,
          height: '100%',
          borderRadius: '10px',
          width: `${((flowState.currentStepIndex + 1) / interactionFlow.length) * 100}%`,
          transition: 'width 0.5s ease',
        }} />
      </div>

      {/* Flow steps */}
      <div style={{
        maxHeight: '400px',
        overflowY: 'auto',
        paddingRight: '5px',
      }}>
        {interactionFlow.map((step, index) => {
          const agentInfo = getAgentInfo(step.agent);
          const isActive = flowState.activeStep?.stepId === step.stepId;
          const isCompleted = flowState.completedSteps.includes(step.stepId);
          const isPending = index > flowState.currentStepIndex;

          return (
            <div
              key={step.stepId}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                marginBottom: '15px',
                opacity: isPending ? 0.4 : 1,
                transition: 'all 0.3s ease',
              }}
            >
              {/* Timeline connector */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginRight: '15px',
                minWidth: '30px',
              }}>
                {/* Step indicator */}
                <div style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: isCompleted 
                    ? '#27ae60' 
                    : isActive 
                    ? getActionColor(step.action)
                    : '#dee2e6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.7em',
                  color: 'white',
                  fontWeight: 'bold',
                  border: isActive ? '3px solid rgba(255,255,255,0.8)' : 'none',
                  boxShadow: isActive ? '0 0 0 3px rgba(52, 152, 219, 0.3)' : 'none',
                  animation: isActive ? 'pulse 1.5s infinite' : 'none',
                }}>
                  {isCompleted ? '✓' : index + 1}
                </div>
                
                {/* Connector line */}
                {index < interactionFlow.length - 1 && (
                  <div style={{
                    width: '2px',
                    height: '40px',
                    background: isCompleted ? '#27ae60' : '#dee2e6',
                    marginTop: '5px',
                    transition: 'background 0.3s ease',
                  }} />
                )}
              </div>

              {/* Step content */}
              <div style={{
                flex: 1,
                background: isActive 
                  ? `${getActionColor(step.action)}15` 
                  : isCompleted 
                  ? '#f8f9fa' 
                  : '#ffffff',
                border: isActive 
                  ? `2px solid ${getActionColor(step.action)}` 
                  : '1px solid #dee2e6',
                borderRadius: '10px',
                padding: '12px',
                transition: 'all 0.3s ease',
              }}>
                {/* Agent and action header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '8px',
                }}>
                  <span style={{ 
                    fontSize: '1.2em', 
                    marginRight: '8px',
                    filter: agentInfo.isOrchestrator ? 'drop-shadow(0 1px 2px rgba(102, 126, 234, 0.5))' : 'none',
                  }}>
                    {agentInfo.avatar}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontWeight: 'bold',
                      fontSize: '0.9em',
                      color: agentInfo.isOrchestrator ? '#667eea' : '#2c3e50',
                    }}>
                      {agentInfo.name}
                    </div>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: getActionColor(step.action),
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '0.75em',
                    fontWeight: 'bold',
                  }}>
                    <span style={{ marginRight: '4px' }}>
                      {getActionIcon(step.action)}
                    </span>
                    {step.action.replace('_', ' ')}
                  </div>
                </div>

                {/* Step description */}
                <div style={{
                  fontSize: '0.85em',
                  color: '#495057',
                  lineHeight: '1.4',
                  marginBottom: '8px',
                }}>
                  {step.description}
                </div>

                {/* Duration and dependencies */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '0.75em',
                  color: '#6c757d',
                }}>
                  <div>
                    ⏱️ {step.duration}ms
                  </div>
                  {step.dependencies.length > 0 && (
                    <div>
                      Depends on: {step.dependencies.join(', ')}
                    </div>
                  )}
                </div>

                {/* Active step progress */}
                {isActive && (
                  <div style={{
                    marginTop: '10px',
                    background: 'rgba(255,255,255,0.8)',
                    borderRadius: '6px',
                    padding: '8px',
                    border: '1px solid rgba(0,0,0,0.1)',
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '0.8em',
                      color: getActionColor(step.action),
                      fontWeight: 'bold',
                    }}>
                      <span style={{ 
                        marginRight: '6px',
                        animation: 'spin 1s linear infinite',
                      }}>
                        ⚙️
                      </span>
                      Processing...
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Completion indicator */}
      {flowState.isComplete && (
        <div style={{
          marginTop: '20px',
          padding: '15px',
          background: 'linear-gradient(135deg, #2ecc71, #27ae60)',
          color: 'white',
          borderRadius: '10px',
          textAlign: 'center',
          fontWeight: 'bold',
          boxShadow: '0 4px 15px rgba(46, 204, 113, 0.3)',
        }}>
          🎉 Orchestration Flow Complete!
        </div>
      )}
    </div>
  );
};

export default InteractionFlowVisualizer;